import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConsentWidgetItemModel, SignConsentRequest } from '@models';
import { ConsentShownInEnum } from '@models';
import { LangEnum } from '@models/enums';
import {
    useGetConsentQuery,
    useSignConsentMutation,
} from '@modules/docs-verification-widget/api/sdk-api';
import { useAppSelector } from '@store';

export const getConsentCheckboxId = (item: ConsentWidgetItemModel, key: string) =>
    `${item.item_id}.${key}`;

export const getConsentItemLocale = (item: ConsentWidgetItemModel, lang: LangEnum) =>
    item.texts[lang] ? lang : LangEnum.EN;

export const useStartConsent = () => {
    const { i18n } = useTranslation();
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const isEnabled = !!sdkOptions?.settings?.consent_settings?.enabled && !sdkOptions.isDemo;

    const { data, isFetching, isError, refetch } = useGetConsentQuery(
        { url: sdkOptions?.apiUrl ?? '', id: sdkOptions?.id ?? '' },
        { skip: !isEnabled },
    );
    const [signConsent] = useSignConsentMutation();
    const [checkedAt, setCheckedAt] = useState<Record<string, string>>({});

    const isRequired = isEnabled && !data?.consent_given;
    const lang = useMemo(
        () =>
            data?.locales.includes(i18n.language as LangEnum)
                ? (i18n.language as LangEnum)
                : LangEnum.EN,
        [data?.locales, i18n.language],
    );

    const isReady =
        !isRequired ||
        (!!data &&
            data.items.every((item) =>
                item.checkboxes.every(
                    (checkbox) =>
                        !checkbox.required || !!checkedAt[getConsentCheckboxId(item, checkbox.key)],
                ),
            ));

    const toggle = (id: string) =>
        setCheckedAt((prev) => {
            const next = { ...prev };

            if (next[id]) {
                delete next[id];
            } else {
                next[id] = new Date().toISOString();
            }

            return next;
        });

    const sign = async () => {
        if (!isRequired || !data || !sdkOptions) {
            return;
        }

        const body: SignConsentRequest = {
            shown_in: sdkOptions.consentShownIn ?? ConsentShownInEnum.KYC_WIDGET,
            locale: lang,
            items: data.items.map((item) => {
                const locale = getConsentItemLocale(item, lang);
                const text = item.texts[locale];

                return {
                    item_id: item.item_id,
                    version: item.version,
                    locale,
                    long: text?.long ?? '',
                    short: text?.short ?? '',
                    checkboxes: item.checkboxes.map((checkbox) => {
                        const checkedTime = checkedAt[getConsentCheckboxId(item, checkbox.key)];

                        return {
                            key: checkbox.key,
                            text: text?.checkboxes[checkbox.key] ?? '',
                            checked: !!checkedTime,
                            ...(checkedTime && { checked_at: checkedTime }),
                        };
                    }),
                };
            }),
        };

        await signConsent({ url: sdkOptions.apiUrl, id: sdkOptions.id, body }).unwrap();
        refetch();
    };

    return {
        isBlocking: (isEnabled && (isFetching || isError)) || !isReady,
        isRequired,
        isError: isEnabled && isError,
        data,
        lang,
        checkedAt,
        toggle,
        sign,
        refetch,
    };
};

export type StartConsent = ReturnType<typeof useStartConsent>;
